// src/utils/pdfExtractor.js - LEGACY VERSION (Most Reliable)
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';

// Set worker using the bundled entry point
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

/**
 * Extracts text content from a PDF file
 * @param {File} file - The PDF file to extract text from
 * @returns {Promise<string>} - Extracted text content
 */
export const extractTextFromPDF = async (file) => {
  try {
    // Validate file type
    if (!file.type.includes('pdf')) {
      throw new Error('Please upload a valid PDF file');
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('PDF file is too large. Maximum size is 10MB');
    }

    // Read the file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    
    // Load the PDF document
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    
    console.log(`PDF loaded. Total pages: ${pdf.numPages}`);
    
    // Extract text from all pages
    let fullText = '';
    
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      
      // Combine all text items with spaces
      const pageText = textContent.items
        .map(item => item.str)
        .join(' ');
      
      fullText += pageText + '\n\n';
      
      console.log(`Extracted page ${pageNum}/${pdf.numPages}`);
    }
    
    // Clean up the text
    fullText = fullText
      .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
      .replace(/\n\s*\n/g, '\n\n')  // Clean up multiple newlines
      .trim();
    
    if (!fullText || fullText.length < 10) {
      throw new Error('No text content found in PDF. The PDF might be image-based or empty.');
    }
    
    console.log(`Successfully extracted ${fullText.length} characters from PDF`);
    
    return fullText;
    
  } catch (error) {
    console.error('PDF extraction error:', error);
    
    // Provide user-friendly error messages
    if (error.message.includes('Invalid PDF')) {
      throw new Error('Invalid PDF file. Please make sure the file is not corrupted.');
    }
    
    if (error.message.includes('password')) {
      throw new Error('This PDF is password-protected. Please use an unprotected PDF.');
    }
    
    throw new Error(error.message || 'Failed to extract text from PDF. Please try again.');
  }
};

/**
 * Validates if a file is a valid PDF
 * @param {File} file - The file to validate
 * @returns {boolean} - True if valid PDF
 */
export const isValidPDF = (file) => {
  if (!file) return false;
  
  // Check file type
  const isCorrectType = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  
  // Check file size (max 10MB)
  const maxSize = 10 * 1024 * 1024;
  const isValidSize = file.size > 0 && file.size <= maxSize;
  
  return isCorrectType && isValidSize;
};

/**
 * Get file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const getFileSizeString = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};
